import React, { useState } from "react";
import { UserPlus, Plus, Users, Building2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Modal } from "../ui/Modal";
import { ActionButton } from "../ui/ActionButton";
import { FormInput, FormSelect } from "../ui/FormControls";

const TYPE_OPTIONS = [
  { value: "individual", label: "Individual", icon: UserPlus, description: "A single person" },
  { value: "household", label: "Household", icon: Users, description: "A couple or family" },
  { value: "organization", label: "Organization", icon: Building2, description: "Company, foundation, etc." },
];

export const AddDonorModal = ({ onClose }) => {
  const { addMember, directors, pathways, palette } = useApp();
  const [formData, setFormData] = useState({
    donorType: "individual",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    pathway: "unengaged",
    isDonor: false,
    isBoardMember: false,
    assignedDirector: "",
    // Household fields
    householdName: "",
    spouseFirstName: "",
    spouseLastName: "",
    spouseEmail: "",
    spousePhone: "",
    // Organization fields
    primaryContactName: "",
  });

  const updateField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const canSubmit = () => {
    switch (formData.donorType) {
      case "organization":
        return formData.organization.trim().length > 0;
      case "household":
        return formData.firstName.trim().length > 0 && formData.lastName.trim().length > 0;
      default:
        return formData.firstName.trim().length > 0 && formData.lastName.trim().length > 0;
    }
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;
    // Auto-generate household name if not provided
    const data = { ...formData };
    if (data.donorType === "household" && !data.householdName.trim() && data.spouseFirstName) {
      data.householdName = `${data.firstName} & ${data.spouseFirstName} ${data.lastName}`.trim();
    }
    addMember(data);
    onClose();
  };

  const isHousehold = formData.donorType === "household";
  const isOrg = formData.donorType === "organization";

  return (
    <Modal title="Add New Donor" icon={UserPlus} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Donor Type Selector */}
        <div>
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: palette.text2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 6,
            }}
          >
            Donor Type
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {TYPE_OPTIONS.map((opt) => {
              const selected = formData.donorType === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => updateField("donorType", opt.value)}
                  style={{
                    padding: "10px 8px",
                    borderRadius: 8,
                    border: `2px solid ${selected ? palette.accent1 : palette.cardBorder}`,
                    background: selected ? `${palette.accent1}08` : "transparent",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    transition: "all 0.15s",
                  }}
                >
                  <opt.icon
                    size={ICON_SIZES.md}
                    style={{ color: selected ? palette.accent1 : palette.text3 }}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 700,
                      color: selected ? palette.accent1 : palette.text1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {opt.label}
                  </span>
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {opt.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Organization Name — only for orgs, shown prominently */}
        {isOrg && (
          <FormInput
            label="Organization Name"
            value={formData.organization}
            onChange={(v) => updateField("organization", v)}
            required
          />
        )}

        {/* Primary person name — Individual & Household */}
        {!isOrg && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FormInput
              label="First Name"
              value={formData.firstName}
              onChange={(v) => updateField("firstName", v)}
              required
            />
            <FormInput
              label="Last Name"
              value={formData.lastName}
              onChange={(v) => updateField("lastName", v)}
              required
            />
          </div>
        )}

        {/* Organization — contact person (optional name + org) */}
        {isOrg && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FormInput
              label="Contact First Name"
              value={formData.firstName}
              onChange={(v) => updateField("firstName", v)}
            />
            <FormInput
              label="Contact Last Name"
              value={formData.lastName}
              onChange={(v) => updateField("lastName", v)}
            />
          </div>
        )}

        {/* Contact info */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormInput
            label={isOrg ? "Organization Email" : "Email"}
            value={formData.email}
            onChange={(v) => updateField("email", v)}
            type="email"
          />
          <FormInput
            label={isOrg ? "Organization Phone" : "Phone"}
            value={formData.phone}
            onChange={(v) => updateField("phone", v)}
          />
        </div>

        {/* Organization for individuals */}
        {!isOrg && (
          <FormInput
            label="Organization"
            value={formData.organization}
            onChange={(v) => updateField("organization", v)}
          />
        )}

        {/* Primary Contact Name — org only, free text */}
        {isOrg && (
          <FormInput
            label="Primary Contact Name"
            value={formData.primaryContactName}
            onChange={(v) => updateField("primaryContactName", v)}
            placeholder="e.g., Jane Smith"
          />
        )}

        {/* Household fields */}
        {isHousehold && (
          <>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: palette.text3,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                paddingBottom: 4,
                borderBottom: `1px solid ${palette.cardBorder}`,
              }}
            >
              Spouse Information
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FormInput
                label="Spouse First Name"
                value={formData.spouseFirstName}
                onChange={(v) => updateField("spouseFirstName", v)}
              />
              <FormInput
                label="Spouse Last Name"
                value={formData.spouseLastName}
                onChange={(v) => updateField("spouseLastName", v)}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FormInput
                label="Spouse Email"
                value={formData.spouseEmail}
                onChange={(v) => updateField("spouseEmail", v)}
                type="email"
              />
              <FormInput
                label="Spouse Phone"
                value={formData.spousePhone}
                onChange={(v) => updateField("spousePhone", v)}
              />
            </div>
            <FormInput
              label="Household Name (for receipts)"
              value={formData.householdName}
              onChange={(v) => updateField("householdName", v)}
              placeholder="e.g., Jerry & Sharon Melson (auto-fills if blank)"
            />
          </>
        )}

        {/* Assignment */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormSelect
            label="Pathway"
            value={formData.pathway}
            onChange={(v) => updateField("pathway", v)}
            options={pathways.map((p) => ({ value: p.id, label: p.name }))}
          />
          <FormSelect
            label="Director"
            value={formData.assignedDirector}
            onChange={(v) => updateField("assignedDirector", v)}
            options={[
              { value: "", label: "Unassigned" },
              ...(directors || []).map((d) => ({ value: d.name, label: d.name })),
            ]}
          />
        </div>

        {/* Flags */}
        <div style={{ display: "flex", gap: 20 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={formData.isDonor}
              onChange={(e) => updateField("isDonor", e.target.checked)}
            />
            Donor
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={formData.isBoardMember}
              onChange={(e) => updateField("isBoardMember", e.target.checked)}
            />
            Board Member
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onClick={onClose} />
          <ActionButton
            label={isOrg ? "Add Organization" : isHousehold ? "Add Household" : "Add Donor"}
            icon={Plus}
            onClick={handleSubmit}
            disabled={!canSubmit()}
          />
        </div>
      </div>
    </Modal>
  );
};
